import { Location } from "@angular/common";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { Component, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { OrdersService } from "src/app/features/admin";
import { PaymentMethod } from "src/app/core/enums/payment-method";
import { PaymentPayload } from "src/app/shared/interfaces";
import { AuthService, ServicePayment, fallbackOnError } from "src/app/core";
import { ButtonComponent, IsEnglishDirective, PaymentService } from "src/app/shared";

@Component({
  selector: "app-invoice",
  standalone: true,
  imports: [TranslateModule, IsEnglishDirective, ButtonComponent],
  templateUrl: "./invoice.component.html",
  styleUrl: "./invoice.component.scss",
})
export class InvoiceComponent {
  private router = inject(Router);
  private location = inject(Location);
  private authService = inject(AuthService);
  private ordersService = inject(OrdersService);
  private activatedRoute = inject(ActivatedRoute);
  private paymentService = inject(PaymentService);

  isFailedPayment = this.location.path().includes("failed");
  isEnglish = true;
  invoiceId = this.activatedRoute.snapshot.params["id"];
  invoiceData = toSignal(this.ordersService.getInvoiceByOrderId(this.invoiceId).pipe(fallbackOnError(undefined)));

  get totalAmount() {
    return this.invoiceData()?.totalPrice;
  }

  get totalDiscount() {
    return this.invoiceData()?.totalDiscount;
  }

  get vat() {
    return this.invoiceData()?.vat;
  }

  get totalAfterDiscount() {
    return this.invoiceData()?.totalAfterDiscount;
  }

  get totalBeforeDiscount() {
    return this.invoiceData()?.totalBeforeDiscount;
  }

  get orderItems() {
    return this.invoiceData()?.orderItems;
  }

  readonly #isGuest = this.authService.isGuest;
  readonly #isAdmin = this.authService.isAdmin;
  readonly #isCounter = this.authService.isCounter;

  /**
   * Formats a given amount string to a US-style number format.
   *
   * @param amount - The amount to be formatted as a string.
   * @returns A formatted string representation of the amount, or "0" if the input is falsy.
   */
  constructAmount(amount: string | number): string {
    if (!amount) return "0";

    return new Intl.NumberFormat("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(amount));
  }

  pay() {
    const body: PaymentPayload = {
      serviceId: `${this.invoiceData()?.id}`,
      paymentMethod: PaymentMethod.Online,
      servicePayment: ServicePayment.Products,
    };

    this.addPayment(body);
  }

  addPayment(body: Partial<PaymentPayload>) {
    this.paymentService.addPayment(body).subscribe();
  }

  windowPrint() {
    window.print();
  }

  /**
   * @description Navigate to the main page
   */
  goHome() {
    // Same as the ticket screen: for a guest this is where the session ends, and it ends through
    // `AuthService` so the cached reads go with it (T-13).
    if (this.#isGuest()) this.authService.logOut();
    localStorage.removeItem("orderItems");
    this.router.navigateByUrl(this.#isCounter() || this.#isAdmin() ? "/admin/booking-services" : "/");
  }
}
